import { CurrencyPipe, DatePipe, DecimalPipe, registerLocaleData } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import localeFr from "@angular/common/locales/fr";
import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom, LOCALE_ID } from "@angular/core";
import { MatNativeDateModule, MatRippleModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogModule } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { EffectsModule } from "@ngrx/effects";
import {
	FullRouterStateSerializer,
	RouterStateSerializer,
	StoreRouterConnectingModule
} from "@ngrx/router-store";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import {
	AutoLoginPartialRoutesGuard,
	provideAuth,
	StsConfigLoader,
	StsConfigStaticLoader
} from "angular-auth-oidc-client";
import { PlotlyViaWindowModule } from "angular-plotly.js";
import { ToastrModule } from "ngx-toastr";

import { AppComponent } from "./app/app.component";
import { SidebarService } from "./app/components/sidebar/sidebar.service";
import { FeedbackStatusColorPipe } from "./app/pages/common/feedback/feedback-status-color.pipe";
import { AppDatePipe } from "./app/pipes/appDatePipe";
import { routerConfig } from "./app/router.config";
import { AppConfigService } from "./app/services/app.config.service";
import { AuthBootstrapService } from "./app/services/auth/auth-bootstrap.service";
import { StaqHttpInterceptor } from "./app/services/auth/staq-http-interceptor";
import { FormValidationService } from "./app/services/form-validation.service";
import { ConsumerAdminGuard } from "./app/services/guards/consumer-guards/consumer-admin.guard";
import { ConsumerBillingAdminGuard } from "./app/services/guards/consumer-guards/consumer-billing-admin.guard";
import { ConsumerExchangeAdminGuard } from "./app/services/guards/consumer-guards/consumer-exchange-admin.guard";
import { ConsumerSubAdminGuard } from "./app/services/guards/consumer-guards/consumer-sub-admin.guard";
import { ActiveUserGuard } from "./app/services/guards/general/active-user-guard";
import { MarketplaceAdminGuard } from "./app/services/guards/marketplace-admin.guard";
import { MarketplaceAnyAdminGuard } from "./app/services/guards/marketplace-any-admin.guard";
import { MarketplaceSuperAdminGuard } from "./app/services/guards/marketplace-super-admin.guard";
import { ExchangeAdminGuard } from "./app/services/guards/vendor-guards/exchange-admin.guard";
import { ProductAdminGuard } from "./app/services/guards/vendor-guards/product-admin.guard";
import { VendorAdminGuard } from "./app/services/guards/vendor-guards/vendor-admin.guard";
import { VendorBillingAdminGuard } from "./app/services/guards/vendor-guards/vendor-billing-admin.guard";
import { InitialNavigationService } from "./app/services/initial-navigation.service";
import { SessionTimeoutService } from "./app/services/session-timeout.service";
import { TypedAppConfigService } from "./app/services/typed-app.config.service";
import { rootEffects } from "./app/store/effects";
import { RouterUrlStateSerializer } from "./app/store/models/router-url-state-serializer";
import { metaReducers, reducers, REDUCERS_TOKEN } from "./app/store/reducers";
import { environment } from "./environments/environment";
import { GlobalErrorHandler } from "./global-error-handler";

// Snapshot the loaded document URL before bootstrap / router navigation so the
// purchase-order wizard can tell a hard refresh from an in-app "Create Order".
InitialNavigationService.capture();

if (environment.production) {
	enableProdMode();
}

registerLocaleData(localeFr);

// const langMap = {
// 	'en-US': 'en',
// 	'en': 'en',
// 	'fr-FR': 'fr',
// 	'fr': 'fr'
// };
// const userLang = navigator.language || 'en-US';
// const locale = langMap[userLang] || 'en';
const locale = "en"; // FORCE ENGLISH FOR NOW

const openIdAuthFactory = (typedAppConfig: TypedAppConfigService) => {
	const config = typedAppConfig.getConfigValue("OPEN_ID_CONFIG");
	return new StsConfigStaticLoader(config);
};

bootstrapApplication(AppComponent, {
	providers: [
		importProvidersFrom(
			StoreModule.forRoot(REDUCERS_TOKEN, {
				metaReducers,
				runtimeChecks: {
					strictStateImmutability: true,
					strictActionImmutability: true
				}
			}),
			EffectsModule.forRoot(rootEffects),
			StoreRouterConnectingModule.forRoot({ serializer: FullRouterStateSerializer }),
			StoreDevtoolsModule.instrument({
				maxAge: 25,
				logOnly: environment.production,
				features: {
					pause: false,
					lock: true,
					persist: true,
					export: false,
					import: false,
					jump: true,
					skip: true,
					reorder: true,
					dispatch: true,
					test: false
				},
				serialize: {
					options: {
						undefined: true,
						error: (err: any) => ({
							name: err.name,
							message: err.message,
							stack: err.stack
						})
					}
				},
				connectInZone: true
			}),
			ToastrModule.forRoot(),
			MatExpansionModule,
			MatNativeDateModule,
			PlotlyViaWindowModule,
			MatRippleModule,
			MatDatepickerModule,
			MatDialogModule
		),
		TypedAppConfigService,
		AppConfigService,
		AutoLoginPartialRoutesGuard,
		ExchangeAdminGuard,
		FormValidationService,
		ActiveUserGuard,
		ProductAdminGuard,
		SessionTimeoutService,
		VendorAdminGuard,
		VendorBillingAdminGuard,
		ConsumerSubAdminGuard,
		ConsumerExchangeAdminGuard,
		ConsumerBillingAdminGuard,
		ConsumerAdminGuard,
		MarketplaceAdminGuard,
		MarketplaceAnyAdminGuard,
		MarketplaceSuperAdminGuard,
		SidebarService,
		CurrencyPipe,
		FeedbackStatusColorPipe,
		DatePipe,
		AppDatePipe,
		DecimalPipe,
		{ provide: ErrorHandler, useClass: GlobalErrorHandler },
		{ provide: HTTP_INTERCEPTORS, useClass: StaqHttpInterceptor, multi: true },
		{ provide: RouterStateSerializer, useClass: RouterUrlStateSerializer },
		{ provide: REDUCERS_TOKEN, useValue: reducers },
		{ provide: LOCALE_ID, useValue: locale },
		provideAnimations(),
		provideHttpClient(withInterceptorsFromDi()),
		provideRouter(routerConfig),
		provideServiceWorker("ngsw-worker.js", {
			enabled: !environment.local,
			registrationStrategy: "registerImmediately"
		}),
		provideTranslateService({
			loader: provideTranslateHttpLoader({
				prefix: "assets/i18n/",
				suffix: ".json"
			}),
			fallbackLang: "en",
			lang: locale
		}),
		provideAuth({
			loader: {
				provide: StsConfigLoader,
				useFactory: openIdAuthFactory,
				deps: [TypedAppConfigService]
			}
		}),
		{
			provide: APP_INITIALIZER,
			useFactory: (authBootstrap: AuthBootstrapService) => () => authBootstrap.init(),
			deps: [AuthBootstrapService],
			multi: true
		}
	]
}).catch((err) => console.error(err));
